import { seoManager } from './seoUtils';
import { seoAnalytics } from './seoAnalytics';
import { performanceOptimizer } from './performanceOptimizer';

export class SEOInitializer {
  private static instance: SEOInitializer;

  static getInstance(): SEOInitializer {
    if (!SEOInitializer.instance) {
      SEOInitializer.instance = new SEOInitializer();
    }
    return SEOInitializer.instance;
  }

  // Initialize all SEO components
  async initialize(): Promise<void> {
    try {
      // Initialize performance optimizations
      this.initializePerformance();

      // Initialize analytics
      this.initializeAnalytics();

      // Add structured data for the website
      this.addWebsiteStructuredData();

      // Initialize resource hints
      this.addResourceHints();

      console.log('SEO initialization completed successfully');
    } catch (error) {
      console.error('Error initializing SEO:', error);
    }
  }

  private initializePerformance(): void {
    // Add resource hints (safe for all environments)
    performanceOptimizer.addResourceHints();

    // Skip problematic preloading features to avoid console errors
    // performanceOptimizer.preloadCriticalResources();
    // performanceOptimizer.setupModulePreloading();

    // Measure Core Web Vitals (safe and useful)
    performanceOptimizer.measureCoreWebVitals();

    // Register service worker for caching (safe for production)
    performanceOptimizer.registerServiceWorker();
  }

  private initializeAnalytics(): void {
    // Initialize Google Analytics 4 if enabled
    const gaId = process.env.VUE_APP_GA_MEASUREMENT_ID;
    if (gaId) {
      seoAnalytics.initializeGA4(gaId);
    }

    // Initialize Search Console
    seoAnalytics.initializeSearchConsole();

    // Track Core Web Vitals
    seoAnalytics.trackCoreWebVitals();
  }

  private addWebsiteStructuredData(): void {
    const websiteStructuredData = seoManager.generateWebsiteStructuredData();
    seoManager.injectStructuredData(websiteStructuredData, 'website-structured-data');
  }

  private addResourceHints(): void {
    // DNS prefetch for external domains
    this.addDNSPrefetch('https://fonts.googleapis.com');
    this.addDNSPrefetch('https://www.google-analytics.com');
    this.addDNSPrefetch('https://pagead2.googlesyndication.com');

    // Preconnect to critical origins
    this.addPreconnect('https://fonts.gstatic.com');
    this.addPreconnect('https://firebasestorage.googleapis.com');
  }

  private addDNSPrefetch(href: string): void {
    const link = document.createElement('link');
    link.rel = 'dns-prefetch';
    link.href = href;
    document.head.appendChild(link);
  }

  private addPreconnect(href: string): void {
    const link = document.createElement('link');
    link.rel = 'preconnect';
    link.href = href;
    link.crossOrigin = 'anonymous';
    document.head.appendChild(link);
  }

  // Add JSON-LD structured data for breadcrumbs
  addBreadcrumbStructuredData(breadcrumbs: Array<{ name: string, url: string }>): void {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "name": crumb.name,
        "item": crumb.url
      }))
    };

    seoManager.injectStructuredData(JSON.stringify(structuredData), 'breadcrumb-structured-data');
  }

  // Add FAQ structured data for better search results
  addFAQStructuredData(faqs: Array<{ question: string, answer: string }>): void {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqs.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer
        }
      }))
    };

    seoManager.injectStructuredData(JSON.stringify(structuredData), 'faq-structured-data');
  }

  // Add organization structured data
  addOrganizationStructuredData(): void {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "WallpaperHub",
      "description": "Free HD wallpapers and backgrounds for desktop, mobile, and tablet",
      "url": window.location.origin,
      "logo": `${window.location.origin}/images/logo.png`,
      "sameAs": [
        // Add your social media URLs here
        // "https://twitter.com/wallpaperhub",
        // "https://facebook.com/wallpaperhub"
      ],
      "contactPoint": {
        "@type": "ContactPoint",
        "contactType": "customer service",
        "url": `${window.location.origin}/contact`
      }
    };

    seoManager.injectStructuredData(JSON.stringify(structuredData), 'organization-structured-data');
  }

  // Update meta tags for specific pages
  updatePageMeta(title: string, description: string, keywords: string, ogImage?: string): void {
    const metaTags = {
      title,
      description,
      keywords,
      ogImage,
      ogType: 'website',
      robots: 'index, follow'
    };

    seoManager.applyMetaTags(metaTags);
  }

  // Track page view for analytics
  trackPageView(pageName: string, category?: string): void {
    seoAnalytics.trackEngagement('page_view', category || 'General', pageName);
  }
}

// Export singleton instance
export const seoInitializer = SEOInitializer.getInstance();